import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';

import type { StockUpdate, StocksParams } from 'app/typings/WelcomeKit/stocks';
import type { PauseReason } from 'app/typings/WelcomeKit/welcomeKit';
import { apiMutation, apiRequest } from 'app/utils/request/api';

import { pathStocks } from '../useGetWelcomeKitLists';

const pausePath =
  '/gateway/hotel-logistics/welcome-kit/hotel-stocks/:hotelId/pause' as const;
const resumePath =
  '/gateway/hotel-logistics/welcome-kit/hotel-stocks/:hotelId/resume' as const;
const editPath =
  '/gateway/hotel-logistics/welcome-kit/hotel-stocks/:hotelId/quantity' as const;
const editMaximumPath =
  '/gateway/hotel-logistics/welcome-kit/hotel-stocks/:hotelId/max-quantity' as const;

const stockUpdatePath =
  '/gateway/hotel-logistics/welcome-kit/hotel-stocks/:hotelId/stock-updates' as const;

export function usePauseStocks(hotelId: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: { reason: PauseReason }) =>
      apiMutation(
        'POST',
        {
          path: pausePath,
          params: { hotelId: hotelId.toString() },
        },
        payload
      ) as Promise<void>,
    onSuccess: () => {
      queryClient.invalidateQueries([pathStocks]);
      message.success(`Hotel #${hotelId} deliverability successfully paused`);
    },
    onError: () => {
      message.error('Error paused deliverability');
    },
  });
}

export function useResumeStocks() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (hotelId: number) =>
      apiMutation('POST', {
        path: resumePath,
        params: { hotelId: hotelId.toString() },
      }) as Promise<void>,
    onSuccess: (_, hotelId) => {
      queryClient.invalidateQueries([pathStocks]);
      message.success(`Hotel #${hotelId} deliverability successfully resumed`);
    },
    onError: () => {
      message.error('Error resume deliverability');
    },
  });
}

export function useEditHotelStocks(hotelId: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: { quantity: number; userId: number }) =>
      apiMutation(
        'PUT',
        {
          path: editPath,
          params: { hotelId: hotelId.toString() },
        },
        payload
      ) as Promise<void>,
    onSuccess: () => {
      queryClient.invalidateQueries([pathStocks]);
      message.success(`Hotel #${hotelId} stocks successfully edited`);
    },
    onError: () => {
      message.error('Error edit stocks');
    },
  });
}

export function useEditMaxStocks(hotelId: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: { maxQuantity?: number }) =>
      apiMutation(
        'PATCH',
        {
          path: editMaximumPath,
          params: { hotelId: hotelId.toString() },
        },
        payload
      ) as Promise<void>,
    onSuccess: () => {
      queryClient.invalidateQueries([pathStocks]);
      message.success(`Hotel #${hotelId} maximum stocks successfully edited`);
    },
    onError: () => {
      message.error('Error edit maximum stocks');
    },
  });
}

export function useStockUpdateByHotelId(
  hotelId: number,
  queryParams: StocksParams
) {
  const queryKey = [stockUpdatePath, { hotelId, queryParams }];

  return useQuery({
    queryKey,
    queryFn: () =>
      apiRequest({
        path: stockUpdatePath,
        params: { hotelId: hotelId.toString() },
        queryParams,
      }) as Promise<StockUpdate[]>,
    onError: () => {
      message.error('Error fetching last stock update');
    },
  });
}
